/** Mirrors the backend's permission enum (app/staff/permissions.py). */
export const PERMISSIONS = [
  "user.read",
  "provider.read",
  "booking.read",
  "payment.read",
  "audit.read",
  "user.write",
  "booking.write",
  "ticket.read",
  "ticket.write",
  "ticket.assign",
  "kyc.read",
  "kyc.review",
  "catalog.write",
  "service.review",
  "banner.write",
  "home_layout.write",
  "wallet.adjust",
  "refund.issue",
  "config.read",
  "config.write",
  "staff.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type StaffRole =
  | "SUPPORT_L1"
  | "SUPPORT_L2"
  | "KYC_REVIEWER"
  | "CATALOG_MANAGER"
  | "FINANCE"
  | "SUPER_ADMIN";

export type UserStatus =
  | "ACTIVE"
  | "DISABLED"
  | "PENDING"
  // The user asked to delete their account; signing in again restores it.
  | "DELETION_PENDING"
  | "DELETED";
export type VerificationStatus =
  | "PENDING"
  | "VERIFIED"
  | "REJECTED"
  | "PENDING_REVIEW";

export type TicketStatus =
  | "OPEN"
  | "PENDING_CUSTOMER"
  | "IN_PROGRESS"
  | "RESOLVED"
  | "CLOSED";
export type TicketPriority = "LOW" | "NORMAL" | "HIGH" | "URGENT";
export type TicketChannel = "PHONE" | "WHATSAPP" | "EMAIL" | "APP" | "INTERNAL";

export interface Envelope<T> {
  data: T;
  meta?: { page?: number; page_size?: number; total?: number };
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_at: string;
  permissions: Permission[];
  roles: StaffRole[];
}

export interface TotpChallenge {
  totp_required: true;
  totp_enrolled: boolean;
  challenge_token: string;
  expires_at: string;
}

export type LoginResult = TokenResponse | TotpChallenge;

export function isTotpChallenge(result: LoginResult): result is TotpChallenge {
  return (result as TotpChallenge).totp_required === true;
}

export interface StaffMe {
  id: string;
  email: string;
  full_name: string;
  status: "ACTIVE" | "DISABLED" | "INVITED";
  totp_enabled: boolean;
  roles: StaffRole[];
  permissions: Permission[];
  last_login_at: string | null;
  created_at: string;
}

export interface AdminUser {
  id: string;
  phone: string | null;
  email: string | null;
  display_name: string | null;
  status: UserStatus;
  roles: string[];
  created_at: string;
  consumer_profile: {
    full_name: string;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    default_address: string | null;
  } | null;
  provider_profile: {
    id: string;
    business_name: string;
    verification_status: VerificationStatus;
    city: string | null;
    rating_average: number;
    is_online: boolean;
  } | null;
}

export interface AdminProvider {
  id: string;
  user_id: string;
  business_name: string;
  description: string | null;
  provider_type: string;
  phone: string | null;
  email: string | null;
  account_status: UserStatus | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  service_radius_km: number;
  verification_status: VerificationStatus;
  verified_at: string | null;
  rating_average: number;
  rating_count: number;
  is_online: boolean;
  fast_accept: boolean;
  created_at: string;
}

export type ServiceReviewStatus = "PENDING" | "APPROVED" | "REJECTED";

/** A provider's service as the review screen sees it (GET /admin/services). */
export interface ReviewService {
  id: string;
  provider_id: string;
  category_id: string;
  category_name: string | null;
  category_icon_url: string | null;
  title: string;
  description: string | null;
  equipment_tags: string[] | null;
  rate_unit: string;
  base_price: string;
  pricing_type: string;
  currency: string;
  is_active: boolean;
  review_status: ServiceReviewStatus;
  /** The rejection reason the provider sees. */
  review_note: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  provider: AdminProvider | null;
}

export interface KycAttempt {
  id: string;
  provider_type: string;
  status: VerificationStatus;
  masked_identifier: string | null;
  verified_name: string | null;
  verified_mobile: string | null;
  failure_reason: string | null;
  verified_at: string | null;
  created_at: string;
}

export interface KycDetail {
  provider: AdminProvider;
  identity: {
    digilocker_name: string | null;
    digilocker_dob: string | null;
    digilocker_gender: string | null;
    digilocker_masked_aadhaar: string | null;
    digilocker_verified_at: string | null;
    consented_at: string | null;
  };
  attempts: KycAttempt[];
}

export interface TicketMessage {
  id: string;
  author_type: "STAFF" | "USER" | "SYSTEM";
  author_staff_id: string | null;
  author_user_id: string | null;
  body: string;
  is_internal: boolean;
  attachments: Record<string, unknown>[] | null;
  created_at: string;
}

export interface Ticket {
  id: string;
  reference: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  channel: TicketChannel;
  requester_user_id: string | null;
  requester_contact: string | null;
  assignee_staff_id: string | null;
  booking_id: string | null;
  provider_id: string | null;
  category: string | null;
  tags: string[] | null;
  sla_due_at: string | null;
  is_sla_breached: boolean;
  first_response_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  messages?: TicketMessage[];
}

export interface TicketStats {
  by_status: Record<TicketStatus, number>;
  overdue: number;
  unassigned: number;
  total: number;
}

export type SettingValueType =
  | "STRING"
  | "INTEGER"
  | "DECIMAL"
  | "BOOLEAN"
  | "JSON"
  | "SECRET";

export interface SettingRow {
  key: string;
  group: string;
  label: string;
  description: string;
  value_type: SettingValueType;
  value: string;
  is_set: boolean;
  is_secret: boolean;
  is_overridden: boolean;
  requires_restart: boolean;
  choices: string[] | null;
  min_value: string | null;
  max_value: string | null;
  updated_at: string | null;
}

export interface SettingHistoryRow {
  id: string;
  key: string;
  old_value: string | null;
  new_value: string | null;
  is_secret: boolean;
  reason: string | null;
  changed_by_staff_id: string | null;
  created_at: string;
}

export type HomeSectionType =
  | "CATEGORY_GRID"
  | "FEATURED_CATEGORIES"
  | "BANNER_CAROUSEL"
  | "NEARBY_PROVIDERS"
  | "TOP_RATED_PROVIDERS"
  | "ACTIVE_TENDERS"
  | "QUICK_ACTIONS";

export type HomeAudience = "ALL" | "CONSUMER" | "PROVIDER";

export interface HomeSection {
  id: string;
  section_type: HomeSectionType;
  title: string;
  title_hi: string | null;
  subtitle: string | null;
  display_order: number;
  is_active: boolean;
  is_live: boolean;
  audience: HomeAudience;
  max_items: number;
  config: Record<string, unknown> | null;
  starts_at: string | null;
  ends_at: string | null;
  updated_at: string;
}

export interface Category {
  id: string;
  name: string;
  name_hi?: string | null;
  slug: string;
  description?: string | null;
  is_active: boolean;
  is_featured?: boolean;
  display_order?: number;
  icon_url?: string | null;
}

export interface AuditEvent {
  id: string;
  actor_user_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface StaffAccount {
  id: string;
  email: string;
  full_name: string;
  status: "ACTIVE" | "DISABLED" | "INVITED";
  totp_enabled: boolean;
  roles: StaffRole[];
  permissions: Permission[];
  last_login_at: string | null;
  created_at: string;
}

// ── Operations analytics ────────────────────────────────────────────────────

export interface PeriodMetrics {
  placed: number;
  completed: number;
  cancelled: number;
  revenue: number;
  /** Null until at least one order in the window has concluded. */
  completion_rate: number | null;
  new_users: { farmers: number; providers: number };
}

export interface RegionRow {
  region: string;
  orders: number;
  completed: number;
  cancelled: number;
  value: number;
}

export interface CategoryRow {
  category: string;
  orders: number;
  value: number;
}

export interface TrendPoint {
  date: string;
  placed: number;
  completed: number;
  cancelled: number;
}

export interface AnalyticsOverview {
  generated_at: string;
  range: { from: string; to: string };
  filters: { state: string | null; city: string | null };
  periods: {
    today: PeriodMetrics;
    yesterday: PeriodMetrics;
    month: PeriodMetrics;
    year: PeriodMetrics;
  };
  range_totals: PeriodMetrics;
  lifetime: {
    orders: number;
    completed: number;
    cancelled: number;
    active: number;
    farmers: number;
    providers: number;
    verified_providers: number;
    online_providers: number;
    open_tenders: number;
    users: number;
  };
  status_mix: Record<string, number>;
  by_region: RegionRow[];
  by_category: CategoryRow[];
  trend: TrendPoint[];
}

export interface RegionOptions {
  states: string[];
  cities: string[];
}

// ── Service area ────────────────────────────────────────────────────────────

export type GeoLevel = "STATE" | "DISTRICT" | "CITY";

export interface ServiceAreaBoundary {
  code: string;
  name: string;
  level: GeoLevel;
  parent_name: string | null;
  source: string | null;
  updated_at: string;
  vertices: number | null;
  /** [min_lng, min_lat, max_lng, max_lat] */
  bbox: [number, number, number, number] | null;
  valid: boolean;
  /** Listed in SERVICE_AREA_CODES. */
  in_use: boolean;
}

export interface ServiceAreaCode {
  code: string;
  name: string | null;
  status: "ok" | "no_outline" | "unknown_code";
}

export interface ServiceAreaOverview {
  mode: "off" | "monitor" | "enforce";
  buffer_km: number;
  codes: ServiceAreaCode[];
  boundaries: ServiceAreaBoundary[];
  max_vertices: number;
}

export interface GeoLocationHit {
  code: string;
  name: string;
  level: GeoLevel;
  parent_name: string | null;
  has_outline: boolean;
}

export interface ServiceAreaCheck {
  mode: string;
  allowed: boolean;
  area_code: string | null;
  matches: {
    code: string;
    name: string;
    level: GeoLevel;
    inside: boolean;
    in_use: boolean;
  }[];
}
